import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * <recently-viewed-products>
 * Reads recently-viewed product ids from localStorage, fetches the matching
 * product cards via the Search API + Section Rendering API, and injects them
 * into this section. Ordering (most-recent first) is handled server-side in
 * the section using search.terms. Skips in the theme editor so the server-side
 * catalog preview stays visible.
 */
class RecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === 'true') return;
    if (window.Shopify && window.Shopify.designMode) return;
    this.load();
  }

  async load() {
    const { sectionId, currentId } = this.dataset;
    const max = parseInt(this.dataset.max || '4', 10);

    let ids = RecentlyViewed.getProducts().map(String);
    if (currentId) ids = ids.filter((id) => id !== String(currentId));
    ids = ids.slice(0, max);

    if (!ids.length || !sectionId) {
      this.hidden = true;
      return;
    }

    try {
      const url = new URL(Theme.routes.search_url, location.origin);
      // Order of ids here (most-recent first) is preserved by the section,
      // which reorders search.results using search.terms.
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) {
        this.hidden = true;
        return;
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fetched = doc.querySelector('recently-viewed-products');
      const items = fetched ? fetched.querySelectorAll('.resource-list__item') : [];

      if (!fetched || !items.length) {
        this.hidden = true;
        return;
      }

      // Replace this section's contents with the fetched (populated) markup.
      // Custom elements (product cards, carousel, quick-add) upgrade on insert.
      this.replaceChildren(
        ...Array.from(fetched.childNodes).map((node) => document.importNode(node, true))
      );
      this.dataset.loaded = 'true';
      this.hidden = false;
    } catch (error) {
      this.hidden = true;
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
